<script setup lang="ts">
import { HaftarahType, ParashahType } from 'types/types'
import { computed, onMounted, reactive, ref, watchEffect } from 'vue'

import { formattedHaftarahForTeacher, formattedHaftarahSimple } from '@/composables/format'
import { setupAutoSingleSelect } from '@/composables/useAutoSingleSelect'
import store from '@/store'
import { compareYear } from '@/util/Aliyah'

interface Props {
  clearable?: boolean
  disabled?: boolean
  externalSet?: boolean
  haftarah: HaftarahType | null
  only?: Array<HaftarahType> | null
  parashah: ParashahType | null
  preventAutoSingleSelect?: boolean
  remove?: Array<HaftarahType>
  resetSelectionOnMultiple?: boolean
}

const xprops = withDefaults(defineProps<Props>(), {
  clearable: false,
  disabled: false,
  externalSet: false,
  only: null,
  preventAutoSingleSelect: false,
  remove: (): Array<HaftarahType> => [],
  resetSelectionOnMultiple: false,
})

const emit = defineEmits(['update:haftarah'])
const innerHaftarahSelect = ref<HTMLElement | null>(null)

const state = reactive({
  selectedHaftarah: xprops.haftarah as HaftarahType | null,
})

const filteredHaftarot = computed<Array<HaftarahType>>(() => {
  const haftarot = xprops.only ?? store.getters.teacher.haftarot
  if (!haftarot?.length) {
    return []
  }

  // remove all elements from haftarot that are also in this.remove
  const filtered = haftarot.filter((a) => !xprops.remove.some((b) => a.reading.id === b.reading.id))

  return [...filtered].sort((a, b) => parseInt(a.reading.id, 10) - parseInt(b.reading.id, 10) || compareYear(a, b))
})

function focus() {
  innerHaftarahSelect.value?.focus()
}

function clear() {
  state.selectedHaftarah = null
}

defineExpose({ clear, focus })

onMounted(() => {
  store.dispatch.teacher.fetchHaftarot({
    mine: false,
    parashah: xprops.parashah,
  })

  watchEffect(() => {
    if (xprops.externalSet) {
      state.selectedHaftarah = xprops.haftarah
    }
  })

  setupAutoSingleSelect({
    filteredItems: filteredHaftarot,
    preventAutoSelect: () => xprops.preventAutoSingleSelect,
    resetOnMultiple: () => xprops.resetSelectionOnMultiple,
    setSelected: (item) => {
      state.selectedHaftarah = item
    },
  })

  watchEffect(() => {
    if (state.selectedHaftarah) {
      store.dispatch.student.fetchHaftarahSentences({
        haftarah: state.selectedHaftarah,
      })
    }
    emit('update:haftarah', state.selectedHaftarah)
  })
})
</script>

<template>
  <v-autocomplete
    ref="innerHaftarahSelect"
    v-model="state.selectedHaftarah"
    auto-select-first
    :clearable="clearable"
    :disabled="disabled"
    :item-title="
      (haftarah: HaftarahType) => {
        return formattedHaftarahSimple(haftarah)
      }
    "
    :items="filteredHaftarot"
    :label="state.selectedHaftarah ? 'Haftarah' : 'Choose a haftarah'"
    :loading="!filteredHaftarot || !filteredHaftarot.length"
    return-object
    variant="underlined"
  >
    <template #item="{ item, props }">
      <v-list-item v-bind="props" :title="formattedHaftarahForTeacher(item.raw)" />
    </template>

    <template #selection="{ item }">
      <v-list-item :title="formattedHaftarahSimple(item.raw)" />
    </template>
  </v-autocomplete>
</template>
